import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import * as React from "react";
import { Search, X, Package, Loader2, AlertCircle } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { HiddenField } from "@/components/fields/hidden-field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ADDRESS_SEARCH_VALIDATION,
  ADDRESS_SEARCH_TIMING,
  ADDRESS_SEARCH_DEFAULTS,
  KEYBOARD_KEYS,
  ENTRY_METHODS,
} from "@/lib/address-search-constants";
import {
  AddressSearchTimeoutError,
  AddressSearchApiError,
  isAbortError,
  logAddressSearchError,
} from "@/lib/address-search-errors";
import {
  useAddressSearchState,
  useAddressSearchDerivedState,
} from "@/lib/address-search-state";
import type {
  CountryCode,
  AddressComponents,
} from "@/lib/address-search-types";
import type {
  ProductPrediction,
  ProductComponents,
} from "@/lib/product-search-types";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

export type ProductSearchAssistiveMessages = {
  searching?: string;
  noResults?: string;
  error?: string;
  resultsFound?: (count: number) => string;
};

export interface ProductSearchFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Readonly<React.HTMLAttributes<HTMLDivElement>> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  country?: CountryCode;
  searchDebounceMs?: number;
  maxResults?: number;
  apiEndpoint?: string;
  manualEntryCheckboxLabel?: string;
  allowManualEntry?: boolean;
  searchPlaceholder?: string;
  searchLabel?: string;
  productCodeLabel?: string;
  productCodePlaceholder?: string;
  brandLabel?: string;
  brandPlaceholder?: string;
  strengthLabel?: string;
  strengthPlaceholder?: string;
  noResultsMessage?: string;
  errorFallbackMessage?: string;
  errorMessageOverride?: string;
  selectedProductHeading?: string;
  selectedProductDismissLabel?: string;
  selectedProductNotCorrectLabel?: string;
  selectedProductTryAgainLabel?: string;
  inputParamName?: string;
  countryParamName?: string;
  srMessages?: ProductSearchAssistiveMessages;
}

export function ProductSearchField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  country = "US",
  searchDebounceMs = ADDRESS_SEARCH_TIMING.DEFAULT_DEBOUNCE_MS,
  maxResults = ADDRESS_SEARCH_DEFAULTS.MAX_RESULTS,
  apiEndpoint = "/api/product-search",
  manualEntryCheckboxLabel = "Enter product manually",
  allowManualEntry = true,
  searchPlaceholder = "Start typing product name or code...",
  searchLabel = "Search Product",
  productCodeLabel = "Product Code",
  productCodePlaceholder = "Enter product code",
  brandLabel = "Brand/Product Name",
  brandPlaceholder = "Enter product name",
  strengthLabel = "Strength",
  strengthPlaceholder = "Enter strength (e.g., 40mg)",
  noResultsMessage = "No products found. Try entering your product manually using the checkbox below.",
  errorFallbackMessage = "Data failed to load, Try again later",
  errorMessageOverride,
  selectedProductHeading = "Selected Product:",
  selectedProductDismissLabel = "Dismiss product",
  selectedProductNotCorrectLabel = "Not correct?",
  selectedProductTryAgainLabel = "Try again",
  inputParamName = "input",
  countryParamName = "country",
  srMessages,
  className,
  ...htmlProps
}: ProductSearchFieldProps<TFieldValues, TName>) {
  // Use consolidated state management hook
  const { state, actions, refs } = useAddressSearchState();
  const {
    searchQuery,
    predictions: rawPredictions,
    showSuggestions,
    isLoading,
    hasSearched,
    hasError,
    errorMessage,
    selectedAddress,
    manualEntry,
    activeIndex,
    hasTyped,
  } = state;

  // Type cast predictions and selected address for product data
  const predictions = rawPredictions as unknown as ProductPrediction[];
  const selectedProduct =
    selectedAddress as unknown as ProductComponents | null;

  // Destructure refs for easier access
  const debounceTimerRef = refs.debounceTimer;
  const abortControllerRef = refs.abortController;
  const queryCache = refs.queryCache;

  // Additional refs not in state hook
  const searchRef = React.useRef<HTMLDivElement>(null);

  const baseFieldName = name as string;
  const listboxId = React.useId();
  const activeDescendantId =
    activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

  // Use derived state hook for mode and adaptive debounce
  // Type cast selectedProduct for shared hook pattern (safe: hook only checks truthiness)
  const { mode, adaptiveDebounceMs } = useAddressSearchDerivedState(
    selectedProduct as unknown as AddressComponents | null,
    manualEntry,
    hasTyped,
    searchDebounceMs,
  );

  const assistiveMessages = {
    searching: srMessages?.searching ?? "Searching for products...",
    noResults: srMessages?.noResults ?? "No products found",
    error: srMessages?.error ?? "Error loading product results",
    resultsFound:
      srMessages?.resultsFound ??
      ((count: number) =>
        `${count} ${count === 1 ? "product" : "products"} found`),
  } satisfies Required<
    Pick<
      ProductSearchAssistiveMessages,
      "searching" | "noResults" | "error" | "resultsFound"
    >
  >;

  // Use controller for validation field so we can update it properly
  const validationField = useController({
    control,
    name: `${baseFieldName}._validation` as FieldPath<TFieldValues>,
  });

  // Controllers for product fields to allow clearing on manual toggle
  const productCodeController = useController({
    control,
    name: `${baseFieldName}.product.productCode` as FieldPath<TFieldValues>,
  });
  const brandController = useController({
    control,
    name: `${baseFieldName}.product.brand` as FieldPath<TFieldValues>,
  });
  const strengthController = useController({
    control,
    name: `${baseFieldName}.product.strength` as FieldPath<TFieldValues>,
  });
  const mahCountryCodeController = useController({
    control,
    name: `${baseFieldName}.product.mahCountryCode` as FieldPath<TFieldValues>,
  });
  const mahCountryController = useController({
    control,
    name: `${baseFieldName}.product.mahCountry` as FieldPath<TFieldValues>,
  });

  // Controller for entry method to determine how data was entered
  const entryMethodController = useController({
    control,
    name: `${baseFieldName}.entryMethod` as FieldPath<TFieldValues>,
  });

  // Initialize internal state from form values on mount (for step navigation)
  React.useEffect(() => {
    const productCode = productCodeController.field.value;
    const brand = brandController.field.value;
    const strength = strengthController.field.value;
    const entryMethod = entryMethodController.field.value;

    // Check if we have product data from form (indicating previous selection)
    const hasProductData = productCode || brand;

    if (hasProductData) {
      // Reconstruct the selected product from form values
      const productComponents = {
        productCode: productCode || "",
        brand: brand || "",
        strength: strength || "",
        mahCountryCode: mahCountryCodeController.field.value || "",
        mahCountry: mahCountryController.field.value || 0,
      };

      // Set the selected address in internal state
      actions.setSelectedAddress(productComponents as unknown as never);

      // Use entry method to determine if this was manual entry
      const isManualEntry = entryMethod === ENTRY_METHODS.MANUAL;
      actions.setManualEntry(isManualEntry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to avoid conflicts with user interactions

  // Update validation field whenever selection state changes
  React.useEffect(() => {
    let newValue = "";
    if (selectedProduct) {
      newValue = "valid";
    } else if (manualEntry) {
      newValue = "manual";
    }
    validationField.field.onChange(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, manualEntry]);

  // Debounced search function with AbortController for race condition prevention
  const searchProduct = React.useCallback(
    async (searchValue: string, signal: AbortSignal) => {
      // Validate and sanitize input
      const trimmed = searchValue.trim();

      // Validation: Check length constraints using constants
      if (
        !trimmed ||
        trimmed.length < ADDRESS_SEARCH_VALIDATION.MIN_QUERY_LENGTH ||
        trimmed.length > ADDRESS_SEARCH_VALIDATION.MAX_QUERY_LENGTH
      ) {
        actions.setPredictions([]);
        actions.setShowSuggestions(false);
        actions.setLoading(false);
        actions.setHasSearched(false);
        actions.setError(false, null);
        return;
      }

      // Basic sanitization: remove potentially dangerous characters
      const sanitized = trimmed.replaceAll(/[<>]/g, "");

      // Create cache key from sanitized input and country
      const cacheKey = `${sanitized}:${country}`;

      // Check cache first - deduplicate identical queries
      if (queryCache.current.has(cacheKey)) {
        const cachedResults = queryCache.current.get(cacheKey)!;
        actions.setPredictions(cachedResults);
        actions.setShowSuggestions(true);
        actions.setHasSearched(true);
        actions.setError(false, null);
        return;
      }

      actions.setLoading(true);
      actions.setError(false, null);

      try {
        // Create a timeout promise that will reject after configured timeout
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(
              new AddressSearchTimeoutError(
                ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS,
              ),
            );
          }, ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS);
        });

        // Race between fetch and timeout
        const response = await Promise.race([
          fetch(
            `${apiEndpoint}?${inputParamName}=${encodeURIComponent(sanitized)}&${countryParamName}=${country}&limit=${maxResults}`,
            {
              signal, // Pass abort signal to fetch
            },
          ),
          timeoutPromise,
        ]);

        if (!response.ok) {
          throw new AddressSearchApiError(response.status, response.statusText);
        }

        const data = await response.json();

        // Check if request was aborted before updating state
        if (signal.aborted) return;

        if (data.data?.value && data.data.value.length > 0) {
          // Transform API response to ProductPrediction format
          const transformedResults = data.data.value
            .slice(0, maxResults)
            .map((item: ProductPrediction) => ({
              productCode: item.productCode,
              brand: item.brand,
              mahCountry: item.mahCountry,
              mahCountryCode: item.mahCountryCode,
              strText: item.strText,
              structured_formatting: {
                main_text: item.brand,
                secondary_text: `${item.productCode} • ${item.strText}`,
              },
            }));

          // Cache eviction: limit cache size to 100 entries
          if (queryCache.current.size >= 100) {
            const firstKey = queryCache.current.keys().next().value;
            if (firstKey) {
              queryCache.current.delete(firstKey);
            }
          }

          // Cache the results for future identical queries
          queryCache.current.set(cacheKey, transformedResults);

          // Use atomic action for success
          actions.searchSuccess(transformedResults);
        } else {
          // Use atomic action for no results
          actions.searchNoResults();
        }
      } catch (error) {
        // Ignore abort errors (they're expected when cancelling requests)
        if (isAbortError(error)) {
          return;
        }

        // Log the error with context
        logAddressSearchError(error, "searchProduct", {
          searchValue: sanitized,
          country,
        });

        // Use error message override or default fallback
        const finalErrorMessage = errorMessageOverride ?? errorFallbackMessage;

        // Don't update state if request was aborted
        if (!signal.aborted) {
          // Use atomic action for error
          actions.searchError(finalErrorMessage);
        }
      } finally {
        // Only update loading state if not aborted
        if (!signal.aborted) {
          actions.setLoading(false);
        }
      }
    },
    [
      apiEndpoint,
      country,
      maxResults,
      actions,
      queryCache,
      errorMessageOverride,
      errorFallbackMessage,
      inputParamName,
      countryParamName,
    ],
  );

  // Debounced search on query change with request cancellation and adaptive timing
  React.useEffect(() => {
    // Mark that user has started typing (enables faster debounce)
    if (searchQuery && !hasTyped) {
      actions.setHasTyped(true);
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Set new timeout with adaptive debounce
    const timerId = setTimeout(() => {
      searchProduct(searchQuery, abortController.signal);
    }, adaptiveDebounceMs);

    debounceTimerRef.current = timerId;

    // Cleanup: clear timeout and abort request on unmount or query change
    return () => {
      clearTimeout(timerId);
      abortController.abort();
    };
  }, [
    searchQuery,
    adaptiveDebounceMs,
    searchProduct,
    abortControllerRef,
    debounceTimerRef,
    hasTyped,
    actions,
  ]);

  // Handle product selection from dropdown
  const handleSelectProduct = React.useCallback(
    (prediction: ProductPrediction) => {
      const productComponents: ProductComponents = {
        productCode: prediction.productCode,
        brand: prediction.brand,
        strength: prediction.strText,
        mahCountryCode: prediction.mahCountryCode,
        mahCountry: prediction.mahCountry,
      };

      // Update form fields
      productCodeController.field.onChange(productComponents.productCode);
      brandController.field.onChange(productComponents.brand);
      strengthController.field.onChange(productComponents.strength);
      mahCountryCodeController.field.onChange(productComponents.mahCountryCode);
      mahCountryController.field.onChange(productComponents.mahCountry);
      entryMethodController.field.onChange(ENTRY_METHODS.AUTOMATIC);

      // Update internal state
      actions.setSelectedAddress(productComponents as unknown as never);
      actions.setShowSuggestions(false);
      actions.setSearchQuery("");
    },
    [
      productCodeController,
      brandController,
      strengthController,
      mahCountryCodeController,
      mahCountryController,
      entryMethodController,
      actions,
    ],
  );

  // Handle dismiss - clear product selection
  const handleDismiss = React.useCallback(() => {
    // Clear form fields
    productCodeController.field.onChange("");
    brandController.field.onChange("");
    strengthController.field.onChange("");
    mahCountryCodeController.field.onChange("");
    mahCountryController.field.onChange(0);
    entryMethodController.field.onChange("");

    // Reset internal state
    actions.dismissAddress();
  }, [
    productCodeController,
    brandController,
    strengthController,
    mahCountryCodeController,
    mahCountryController,
    entryMethodController,
    actions,
  ]);

  // Handle manual entry toggle
  const handleManualEntryToggle = React.useCallback(
    (checked: boolean) => {
      actions.setManualEntry(checked);

      if (checked) {
        // Set entry method to manual
        entryMethodController.field.onChange(ENTRY_METHODS.MANUAL);
        // Clear search state
        actions.resetSearch();
      } else {
        // Clear manual entry fields
        productCodeController.field.onChange("");
        brandController.field.onChange("");
        strengthController.field.onChange("");
        mahCountryCodeController.field.onChange("");
        mahCountryController.field.onChange(0);
        entryMethodController.field.onChange("");
      }
    },
    [
      actions,
      productCodeController,
      brandController,
      strengthController,
      mahCountryCodeController,
      mahCountryController,
      entryMethodController,
    ],
  );

  // Keyboard navigation for suggestions
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (!showSuggestions || predictions.length === 0) return;

      switch (event.key) {
        case KEYBOARD_KEYS.ARROW_DOWN:
          event.preventDefault();
          actions.setActiveIndex(
            activeIndex < predictions.length - 1 ? activeIndex + 1 : 0,
          );
          break;
        case KEYBOARD_KEYS.ARROW_UP:
          event.preventDefault();
          actions.setActiveIndex(
            activeIndex > 0 ? activeIndex - 1 : predictions.length - 1,
          );
          break;
        case KEYBOARD_KEYS.ENTER:
          event.preventDefault();
          if (activeIndex >= 0 && predictions[activeIndex]) {
            handleSelectProduct(predictions[activeIndex]);
          }
          break;
        case KEYBOARD_KEYS.ESCAPE:
          event.preventDefault();
          actions.setShowSuggestions(false);
          actions.setActiveIndex(-1);
          break;
        case KEYBOARD_KEYS.HOME:
          event.preventDefault();
          actions.setActiveIndex(0);
          break;
        case KEYBOARD_KEYS.END:
          event.preventDefault();
          actions.setActiveIndex(predictions.length - 1);
          break;
      }
    },
    [showSuggestions, predictions, activeIndex, actions, handleSelectProduct],
  );

  // Close suggestions when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        actions.setShowSuggestions(false);
        actions.setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [actions]);

  // Filter out locale to prevent DOM warnings on div element
  const { locale: _locale, ...filteredHtmlProps } =
    htmlProps as React.HTMLAttributes<HTMLDivElement> & { locale?: string };

  return (
    <div className={cn("space-y-4", className)} {...filteredHtmlProps}>
      {/* Main Label */}
      <FormLabel>
        {label}
        {required && (
          <>
            <span className="ms-1 text-red-500" aria-hidden="true">
              *
            </span>
            <span className="sr-only">({requiredLabel})</span>
          </>
        )}
        {description && descriptionAsTooltip && (
          <FormTooltip description={description} />
        )}
      </FormLabel>

      {description && !descriptionAsTooltip && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Search Mode */}
      {mode === "search" && (
        <div ref={searchRef} className="relative">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => actions.setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (predictions.length > 0) {
                  actions.setShowSuggestions(true);
                }
              }}
              className="pl-10"
              aria-label={searchLabel}
              aria-expanded={showSuggestions}
              aria-controls={listboxId}
              aria-activedescendant={activeDescendantId}
              aria-autocomplete="list"
              role="combobox"
            />
            {isLoading && (
              <Loader2 className="absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Screen reader announcements */}
          <div className="sr-only" role="status" aria-live="polite">
            {isLoading && assistiveMessages.searching}
            {hasSearched &&
              !isLoading &&
              predictions.length > 0 &&
              assistiveMessages.resultsFound(predictions.length)}
            {hasSearched &&
              !isLoading &&
              predictions.length === 0 &&
              !hasError &&
              assistiveMessages.noResults}
            {hasError && errorMessage}
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && (
            <div
              id={listboxId}
              role="listbox"
              aria-label="Product suggestions"
              className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-md"
            >
              {hasError && (
                <div className="flex items-start gap-2 p-3 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{errorMessage || errorFallbackMessage}</span>
                </div>
              )}

              {!hasError && predictions.length === 0 && hasSearched && (
                <div className="p-3 text-sm text-muted-foreground">
                  {noResultsMessage}
                </div>
              )}

              {!hasError &&
                predictions.map((prediction, index) => (
                  <div
                    key={prediction.productCode}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    tabIndex={-1}
                    aria-selected={index === activeIndex ? "true" : "false"}
                    className={cn(
                      "cursor-pointer p-3 transition-colors hover:bg-accent",
                      index === activeIndex && "bg-accent",
                    )}
                    onClick={() => handleSelectProduct(prediction)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectProduct(prediction);
                      }
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <Package className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium">
                          {prediction.structured_formatting?.main_text ||
                            prediction.brand}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {prediction.structured_formatting?.secondary_text ||
                            `${prediction.productCode} • ${prediction.strText}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Selected Mode */}
      {mode === "selected" && selectedProduct && (
        <div className="rounded-md border p-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium">{selectedProductHeading}</h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              aria-label={selectedProductDismissLabel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-start gap-2">
              <Package className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{selectedProduct.brand}</div>
                <div className="text-muted-foreground">
                  {selectedProduct.productCode}
                  {selectedProduct.strength && ` • ${selectedProduct.strength}`}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 text-sm text-muted-foreground">
            {selectedProductNotCorrectLabel}{" "}
            <button
              type="button"
              onClick={handleDismiss}
              className="text-primary underline-offset-4 hover:underline"
            >
              {selectedProductTryAgainLabel}
            </button>
          </div>
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === "manual" && (
        <div className="space-y-4">
          <FormField
            control={control}
            name={
              `${baseFieldName}.product.productCode` as FieldPath<TFieldValues>
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {productCodeLabel}
                  {required && <span className="ml-1 text-destructive">*</span>}
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder={productCodePlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={`${baseFieldName}.product.brand` as FieldPath<TFieldValues>}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {brandLabel}
                  {required && <span className="ml-1 text-destructive">*</span>}
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder={brandPlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={
              `${baseFieldName}.product.strength` as FieldPath<TFieldValues>
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel>{strengthLabel}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={strengthPlaceholder} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {/* Manual Entry Checkbox */}
      {allowManualEntry && mode !== "selected" && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${baseFieldName}-manual-entry`}
            checked={manualEntry}
            onCheckedChange={handleManualEntryToggle}
          />
          <Label
            htmlFor={`${baseFieldName}-manual-entry`}
            className="text-sm font-normal"
          >
            {manualEntryCheckboxLabel}
          </Label>
        </div>
      )}

      {/* Hidden fields for form submission */}
      <div className="hidden">
        <HiddenField
          control={control}
          name={`${baseFieldName}._validation` as FieldPath<TFieldValues>}
        />
        {/* Product fields - only use hidden fields when not in manual entry mode to avoid duplicate registration */}
        {mode !== "manual" && (
          <>
            <HiddenField
              control={control}
              name={
                `${baseFieldName}.product.productCode` as FieldPath<TFieldValues>
              }
            />
            <HiddenField
              control={control}
              name={`${baseFieldName}.product.brand` as FieldPath<TFieldValues>}
            />
            <HiddenField
              control={control}
              name={
                `${baseFieldName}.product.strength` as FieldPath<TFieldValues>
              }
            />
          </>
        )}
        <HiddenField
          control={control}
          name={
            `${baseFieldName}.product.mahCountryCode` as FieldPath<TFieldValues>
          }
        />
        <HiddenField
          control={control}
          name={
            `${baseFieldName}.product.mahCountry` as FieldPath<TFieldValues>
          }
        />
        <HiddenField
          control={control}
          name={`${baseFieldName}.entryMethod` as FieldPath<TFieldValues>}
        />
      </div>

      {/* Form validation message */}
      <FormMessage>{validationField.fieldState.error?.message}</FormMessage>
    </div>
  );
}
